import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface FilterBarProps {
  /** The search control, if the list has one. Held to a readable width. */
  search?: ReactNode
  /** The filters, laid out inline in the order they are given. */
  children?: ReactNode
  /** Buttons — export, add, refresh. Pushed to the trailing edge. */
  actions?: ReactNode
  className?: string
}

/**
 * One row above a list: search, then filters, then actions.
 *
 * <p>Every list screen had built this itself, and no two agreed — some stacked a label over
 * each control so the search sat a line higher than the filters beside it, some stretched
 * every select to the container so three short lists became three identical boxes, and the
 * actions landed on the left on one page and the right on the next. The result read as a
 * different product on each screen.
 *
 * <p>The rules this fixes:
 *
 * <ul>
 *   <li><b>one row</b> — search and the first filter share a baseline; the row wraps on a
 *       narrow viewport rather than scrolling sideways;
 *   <li><b>search has a width</b> — capped at 320px, because a search box as wide as a
 *       1,600px monitor looks like a text editor;
 *   <li><b>filters size to their content</b> — the {@code Select} measures its widest
 *       option, so a status filter is status-sized and a department filter is
 *       department-sized;
 *   <li><b>actions trail</b> — {@code ms-auto}, so they mirror under Arabic without a
 *       second layout.
 * </ul>
 *
 * <p>Filters carry their own name in their first option ("Every department", "Any status")
 * rather than a label stacked above them. That is what keeps the row a row.
 */
export function FilterBar({ search, children, actions, className }: FilterBarProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        // A date is a fixed-length value, so a date field in this row is date-sized. It
        // keeps its full width inside a form, where the grid decides the column: the rule
        // belongs to the row, not to the control.
        '[&>div:has(>[data-slot=date-picker-input])]:w-[168px]',
        className,
      )}
    >
      {search && <div className="w-full min-w-[200px] max-w-[320px] sm:w-auto sm:flex-1">{search}</div>}
      {children}
      {actions && <div className="ms-auto flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
